import clsx from 'clsx';
import { AnimatePresence, motion } from 'motion/react';
import { useLocation } from 'react-router-dom';

import { useNote } from '@/hooks/rxdb-models';
import { useDocumentId } from '@/hooks/use-document-id';
import { useNavigateWithSearch } from '@/hooks/use-navigate-with-search';

import { Icon } from '@flintmint/web-shared/components/icon';

import { appRoutes } from '@/constants/routes';

import styles from './back-to-note.module.scss';

export const BackToNote = () => {
  const location = useLocation();
  const navigateWithSearch = useNavigateWithSearch();

  const linkedInstanceNote = location.state?.linkedInstanceNote;

  // The location state is a snapshot taken when "Edit Template" was clicked. The note can be
  // detached (or deleted) afterwards, possibly from another tab, so follow the live document
  // and drop the shortcut once it no longer points at the template on screen.
  const { id: templateId } = useDocumentId();
  const { data: liveNote, isLoading } = useNote(linkedInstanceNote?.id);
  const isStillLinked = isLoading || liveNote?.templateId === templateId;

  return (
    <AnimatePresence key="back-to-note">
      {linkedInstanceNote && isStillLinked && (
        <div className={styles.absoluteHolder}>
          <div className={styles.absoluteContentHolder}>
            <motion.div
              className={styles.backToNote}
              initial={{ bottom: -10, opacity: 0 }}
              animate={{ bottom: 24, opacity: 1 }}
              exit={{ bottom: 0, opacity: 0 }}
              data-testid="back-to-note-holder"
            >
              <button
                onClick={() => {
                  navigateWithSearch(`${appRoutes.library}/${linkedInstanceNote.id}`, {
                    state: {
                      linkedInstanceNote: null,
                    },
                  });
                }}
                aria-label="Back to note"
              >
                <span className={styles.backHolder}>
                  <Icon size={20} className={styles.back} type="arrow-left-02-standard-solid" />
                  <span>
                    <span>Back To Note</span>
                    <span className={clsx(styles.title, 'ellipsis-text')}>{linkedInstanceNote.name}</span>
                  </span>
                </span>
                <Icon
                  size={14}
                  type="cancel-01"
                  className={styles.close}
                  data-testid="close-icon"
                  onClick={(e) => {
                    navigateWithSearch(location.pathname, {
                      replace: true,
                      state: null,
                    });
                    e.stopPropagation();
                  }}
                />
              </button>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};
